import { useSiteContent } from '@/hooks/useSiteContent';
import { useTranslation } from 'react-i18next';
import ContentState from '@/components/feature/ContentState';

interface SixCommitmentsData {
  title: string;
  highlight: string;
  items: { title: string; description: string; image: string; badge: string }[];
}

export default function SixCommitments() {
  const { data, loading, error, refresh } = useSiteContent<SixCommitmentsData>(
    'why_us_six_commitments',
  );
  const { t } = useTranslation();

  if (loading || error || !data) {
    return <ContentState loading={loading} error={error} ready={!!data} onRetry={refresh} />;
  }

  return (
    <section className="w-full bg-background-200 py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 text-center mb-4">
            {data.title}{' '}
            <span className="text-primary-500">{data.highlight}</span>
          </h2>
          <p className="text-center text-foreground-600 max-w-2xl mx-auto mb-12 md:mb-16">
            {t('whyus.commitmentsNote')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {data.items.map((item, idx) => (
              <div
                key={idx}
                className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-colors"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="inline-flex items-center gap-1 bg-primary-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      <i className="ri-shield-check-line text-xs" />
                      {item.badge}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}